import React from 'react'

import HelpTooltip from 'components/common/tooltip'
import Checkbox from 'components/ui/checkbox'
import Label from 'components/ui/label'
import { RadioButton } from 'components/ui/radio'

type Value = string | number | boolean

type Props = {
  disabled?: boolean
  multiple?: boolean
  wrapText?: boolean
  title?: React.ReactNode
  tooltipId?: string
  items: {
    label: React.ReactNode
    value: Value
    selected?: boolean
    partial?: boolean
    disabled?: boolean
    tooltipContent?: React.ReactNode
    nodeBottom?: React.ReactNode
    onChange?: () => any
  }[]
  selected?: string | boolean
  onChange?: (value: Value, checked: boolean) => void
}

/**
 * A flexible choice list component that can render as checkboxes or radio buttons
 * @param disabled - Whether the entire choice list is disabled - defaults to false
 * @param multiple - Whether to allow multiple selections (checkboxes) or single selection (radio buttons) - defaults to true
 * @param wrapText - Whether to wrap text in radio button labels - defaults to false
 * @param title - Optional title/label to display above the choice list
 * @param tooltipId - Optional tooltip ID to show help tooltip next to the title
 * @param items - Array of choice items, each with label, value, and optional properties like selected, disabled, tooltipContent
 * @param selected - Currently selected value(s) - string, boolean, or undefined
 * @param onChange - Callback function called when selection changes, receives (value, checked)
 * @returns A div element containing the choice list with title and items
 */
const ChoiceList = ({
  disabled,
  multiple = true,
  wrapText = false,
  title,
  tooltipId,
  items,
  selected,
  onChange,
}: Props) => (
  <div className="ChoiceList">
    {title && <Label caption={title} tooltipId={tooltipId} />}
    {items.map(item => (
      <div key={String(item.value)} className="ChoiceList_Item">
        {multiple ? (
          <Checkbox
            checked={item.value === selected || item.selected}
            disabled={disabled || item.disabled}
            partial={item.partial} // TODO: Optimize Checkbox change handler (1)
            onChange={(label, checked) =>
              (item.onChange && item.onChange()) ||
              (onChange && onChange(item.value, checked))
            }
          >
            {item.label}
            {item.tooltipContent && (
              <HelpTooltip>{item.tooltipContent}</HelpTooltip>
            )}
          </Checkbox>
        ) : (
          <>
            <RadioButton
              label={item.label}
              checked={item.value === selected || item.selected}
              disabled={disabled || item.disabled} // TODO: Optimize Checkbox change handler (2)
              onChange={(label, checked) =>
                (item.onChange && item.onChange()) ||
                (onChange && onChange(item.value, checked))
              }
              wrapText={wrapText}
            />
            {item.nodeBottom && item.nodeBottom}
          </>
        )}
      </div>
    ))}
  </div>
)

export default ChoiceList
